/**
 * Cheatsheet overlay — '?' toggles a quick reference of shortcuts and the
 * common workflows (many features hide behind right-click / pick modes).
 */

import type { App } from '../app';

const SECTIONS: [string, [string, string][]][] = [
  ['Sketching', [
    ['L / R / C / A / S / T', 'Line · Rect · Circle · Arc · Spline · Text'],
    ['P / O / X', 'Polygon ("r,n") · Slot · Trim (click the piece to remove)'],
    ['right-click → Offset…', 'Parallel line / concentric circle (± shrinks/grows)'],
    ['Enter', 'End line/spline chain, then finish sketch'],
    ['type numbers', 'Exact size while drawing (40, 40x20, expressions)'],
    ['axis inference', 'Lines snap to the red X / green Y axes (guide + "On X axis")'],
    ['drag a point', 'Edit existing entities (constraints re-solve live)'],
    ['right-click entity', 'Constraints, dimensions, delete entity'],
    ['click dimension badge', 'Retype a dimension — numbers or expressions'],
  ]],
  ['Modeling', [
    ['P → drag a cap', 'Push/Pull tool: change an extrude\'s depth (⌥ = taper)'],
    ['Select (default)', 'Click selects · drag → box: whole bodies · drag ← box: touching · ⇧ adds'],
    ['O / ⌥drag / middle-drag', 'Orbit — O arms the Orbit tool; left-drag is the select box'],
    ['Fillet / Chamfer', 'Click edges to pick, Enter applies (none = all)'],
    ['Hole', 'Click positions on a face, Enter drills (M-sizes in Properties)'],
    ['Pattern', 'Linear/circular arrays — count takes expressions'],
    ['Loft', '⇧click 2+ station sketches in order, then Loft'],
    ['Coil', 'Helical spring/thread — radius, pitch, turns in Properties'],
    ['ƒx Parameters', 'Named values; type "width/2" in any numeric field'],
  ]],
  ['Assemblies', [
    ['Projects → ⊞', 'Insert another project as a linked component'],
    ['2 bodies + right-click', 'Mate (stack) · Concentric mate · Check interference'],
    ['right-click body', 'Mass properties: exact volume, area, center of mass'],
    ['edit source project', 'Assemblies update automatically'],
  ]],
  ['Drawings', [
    ['Drawing button', 'Sheet: front/top/right/iso + auto dims'],
    ['Section on first', 'Adds a hatched SECTION view'],
    ['Dimension', 'Click two points for a custom dim (true mm)'],
    ['DXF / PDF', 'Export the sheet'],
  ]],
  ['View & tools', [
    ['M', 'Measure two points (snaps to true vertices)'],
    ['F', 'Fit view · double-click = focus point'],
    ['Section / Curvature', 'Slice the model · fairing color check'],
    ['⌘Z / ⌘⇧Z', 'Undo / redo — every action, including AI edits'],
    ['?', 'This cheatsheet'],
  ]],
];

export function mountCheatsheet(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-cheatsheet';
  panel.style.display = 'none';
  panel.innerHTML =
    '<h3>NovaCAD quick reference <span class="cheat-close">✕</span></h3>' +
    SECTIONS.map(
      ([title, rows]) =>
        `<h4>${title}</h4>` +
        rows
          .map(([k, v]) => `<div class="cheat-row"><kbd>${k}</kbd><span>${v}</span></div>`)
          .join(''),
    ).join('');
  host.appendChild(panel);

  const render = (): void => {
    panel.style.display = app.cheatsheetOpen ? 'block' : 'none';
  };
  panel.querySelector('.cheat-close')?.addEventListener('click', () => {
    app.cheatsheetOpen = false;
    app.notifyUI();
  });
  app.onUI(render);
  render();
}
